import React, { useState, useEffect, useRef } from 'react';
import { noteService, goalService } from '../services/api';
import SideMenu from './SideMenu';
// Comment out ForceGraph2D import as we're switching to list view
// import ForceGraph2D from 'react-force-graph-2d';
import './KnowledgeGraph.css';
// Comment out d3 as it's only needed for the graph view
// import * as d3 from 'd3';
import { useTheme } from '../ThemeContext';

// Import icons to match Home page
import { Search, Plus, ArrowLeft, Edit3, Trash2, Save, X, Hash, FileText, Calendar, Tag } from 'lucide-react';

// We'll implement the force graph visualization in a separate step
// import ForceGraph2D from 'react-force-graph-2d';

const KnowledgeGraph = () => {
  const [notes, setNotes] = useState([]);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [connections, setConnections] = useState({ nodes: [], links: [] });
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [highlightedResultIndex, setHighlightedResultIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Change default viewMode to 'list' from 'graph'
  const [viewMode, setViewMode] = useState('list');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showNotes, setShowNotes] = useState(false); // New state for toggling notes panel
  const [showSearchResults, setShowSearchResults] = useState(false); // New state for controlling search results visibility
  const [selectedTagFilter, setSelectedTagFilter] = useState(null); // New state for tag filtering
  const [dimensions, setDimensions] = useState({ 
    width: document.querySelector('.graph-view')?.clientWidth || 800, 
    height: 500 
  });
  
  const editorRef = useRef(null);
  const graphRef = useRef(null);
  
  // Form state for note editing
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  
  const { theme } = useTheme();
  
  // Function to parse tags from note content
  const parseTagsFromContent = (content) => {
    if (!content) return [];
    
    const tagRegex = /#(\w+)/g;
    const tags = [];
    let match;
    
    while ((match = tagRegex.exec(content)) !== null) {
      const tag = match[1];
      if (!tags.includes(tag)) {
        tags.push(tag);
      }
    }
    
    return tags;
  };
  
  // Function to get tags from note (either from Tags field or parsed from content)
  const getNoteTags = (note) => {
    // First try to get tags from the Tags field (JSON string from backend)
    if (note.tags) {
      try {
        const parsedTags = JSON.parse(note.tags);
        if (Array.isArray(parsedTags) && parsedTags.length > 0) {
          return parsedTags;
        }
      } catch (error) {
        console.warn('Failed to parse tags from note.tags field:', error);
      }
    }
    
    // Fallback to parsing tags from content
    return parseTagsFromContent(note.content);
  };
  
  // Function to get all unique tags from all notes
  const getAllUniqueTags = () => {
    const allTags = [];
    notes.forEach(note => {
      const noteTags = getNoteTags(note);
      noteTags.forEach(tag => {
        if (!allTags.includes(tag)) {
          allTags.push(tag);
        }
      });
    });
    return allTags.sort(); // Sort alphabetically
  };
  
  // Function to handle tag click - set search query and focus search input
  const handleTagClick = (tagName, event) => {
    event.stopPropagation(); // Prevent triggering note selection
    const searchQuery = `#${tagName}`;
    setSearchQuery(searchQuery);
    
    // Focus the search input
    const searchInput = document.querySelector('.search-section input');
    if (searchInput) {
      searchInput.focus();
    }
  };
  
  // Function to handle tag filter click in header
  const handleTagFilterClick = (tagName, event) => {
    event.stopPropagation();
    if (selectedTagFilter === tagName) {
      // If clicking the same tag, clear the filter
      setSelectedTagFilter(null);
    } else {
      // Set the new tag filter
      setSelectedTagFilter(tagName);
    }
  };
  
  // Function to get filtered notes based on selected tag
  const getFilteredNotes = () => {
    if (!selectedTagFilter) {
      return notes; // Return all notes if no filter is selected
    }
    
    return notes.filter(note => {
      const noteTags = getNoteTags(note);
      return noteTags.includes(selectedTagFilter);
    });
  };
  
  // Function to handle search input focus/click - show search results if there's a query
  const handleSearchInputFocus = () => {
    if (searchQuery.trim() && searchResults.length > 0) {
      setShowSearchResults(true);
    }
  };
  
  // Function to handle clicking outside search area to hide results
  const handleClickOutside = (event) => {
    const searchSection = document.querySelector('.search-section');
    if (searchSection && !searchSection.contains(event.target)) {
      setShowSearchResults(false);
    }
  };
  
  // Handle window resize to update graph dimensions
  useEffect(() => {
    const handleResize = () => {
      // Get dimensions from the main content area for fullscreen
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        setDimensions({
          width: mainContent.clientWidth,
          height: mainContent.clientHeight - 60 // Subtract header height
        });
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Call handleResize initially and whenever the sidebar toggles
    handleResize();
    
    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
      // Update dimensions after sidebar toggle
      setTimeout(handleResize, 300); // Wait for animation to complete
    };
    
    document.addEventListener('sidebar-toggle', handleSidebarToggle);
    
    // Add click outside listener for search results
    document.addEventListener('mousedown', handleClickOutside);
    
    // Initial data fetch
    const fetchData = async () => {
      try {
        // Fetch current goal
        const goals = await goalService.getUserGoals();
        if (goals && goals.length > 0) {
          setCurrentGoal(goals[0]);
        }
        
        setLoading(true);
        const notesData = await noteService.getAllNotes();
        let connectionsData = [];
        
        try {
          connectionsData = await noteService.getNoteConnections();
          console.log("Got connections data:", connectionsData);
        } catch (err) {
          console.error('Failed to load connections, using parsed links', err);
          // Generate connections from note content instead
          const generatedConnections = generateConnectionsFromNotes(notesData);
          connectionsData = generatedConnections.links;
        }
        
        setNotes(notesData);
        
        // Transform connections data to match the format expected by ForceGraph
        const graphData = {
          nodes: notesData.map(note => ({
            id: note.id,
            title: note.title || 'Untitled',
            val: 1,
            color: "#c8746d"
          })),
          links: connectionsData.length > 0 
            ? connectionsData.map(connection => ({
                source: connection.sourceId || connection.source,
                target: connection.targetId || connection.target,
                value: connection.strength || connection.value || 1
              }))
            : generateConnectionsFromNotes(notesData).links
        };
        
        setConnections(graphData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load knowledge base data');
        setLoading(false);
        console.error(err);
      }
    };
    
    fetchData();

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const generateConnectionsFromNotes = (notesData) => {
    // Create nodes from notes
    const nodes = notesData.map(note => ({
      id: note.id,
      title: note.title || 'Untitled',
      val: 1,
      color: "#c8746d"
    }));
    
    // Generate connections based on [[]] links in notes
    const links = [];
    const linkRegex = /\[\[(.*?)\]\]/g;
    
    notesData.forEach(sourceNote => {
      let match;
      const content = sourceNote.content || '';
      
      // Find all [[]] links in the note content
      while ((match = linkRegex.exec(content)) !== null) {
        const linkedTitle = match[1].trim();
        
        // Find the target note by title
        const targetNote = notesData.find(n => 
          n.title.toLowerCase() === linkedTitle.toLowerCase()
        );
        
        // If target note exists and it's not the same as source note
        if (targetNote && targetNote.id !== sourceNote.id) {
          // Check if this connection already exists
          const connectionExists = links.some(link => 
            (link.source === sourceNote.id && link.target === targetNote.id) ||
            (link.source === targetNote.id && link.target === sourceNote.id)
          );
          
          if (!connectionExists) {
            links.push({
              source: sourceNote.id,
              target: targetNote.id,
              value: 1
            });
          }
        }
      }
    });
    
    return { nodes, links };
  };
  
  // Perform search whenever the search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery]);

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    // Reset highlighted result when search changes
    setHighlightedResultIndex(-1);
    
    const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/);
    
    // First find exact title matches (highest priority)
    const exactTitleMatches = notes.filter(note => 
      note.title.toLowerCase() === searchQuery.toLowerCase()
    ).map(note => ({
      ...note,
      matchType: 'exact-title'
    }));
    
    // Then find partial title matches
    const partialTitleMatches = notes.filter(note => 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !exactTitleMatches.some(m => m.id === note.id)
    ).map(note => ({
      ...note,
      matchType: 'partial-title'
    }));
    
    // Finally find content matches
    const contentMatches = notes.filter(note => 
      searchTerms.some(term => note.content.toLowerCase().includes(term)) &&
      !exactTitleMatches.some(m => m.id === note.id) &&
      !partialTitleMatches.some(m => m.id === note.id)
    ).map(note => {
      // Find the context of the match in content
      const contentLower = note.content.toLowerCase();
      let matchIndex = -1;
      let matchTerm = '';
      
      for (const term of searchTerms) {
        const index = contentLower.indexOf(term);
        if (index !== -1) {
          matchIndex = index;
          matchTerm = term;
          break;
        }
      }
      
      // Get a snippet of content around the match
      let contentPreview = '';
      if (matchIndex !== -1) {
        const startIndex = Math.max(0, matchIndex - 30);
        const endIndex = Math.min(note.content.length, matchIndex + matchTerm.length + 30);
        contentPreview = note.content.substring(startIndex, endIndex);
        
        // Add ellipsis if we're not at the beginning/end
        if (startIndex > 0) contentPreview = '...' + contentPreview;
        if (endIndex < note.content.length) contentPreview = contentPreview + '...';
      }
      
      return {
        ...note,
        matchType: 'content',
        contentPreview
      };
    });
    
    // Combine results with priority order
    const combinedResults = [
      ...exactTitleMatches,
      ...partialTitleMatches,
      ...contentMatches
    ];
    
    setSearchResults(combinedResults);
  };
  
  const handleSelectNote = (note) => {
    if (note) {
      setSelectedNote(note);
      setNoteTitle(note.title);
      setNoteContent(note.content);
      setIsEditing(false);
      setViewMode('note'); // Switch to note view when a note is selected
      setShowSearchResults(false); // Hide search results when a note is selected
    } else {
      setSelectedNote(null);
    }
  };
  
  const handleCreateNewNote = () => {
    setSelectedNote(null);
    setNoteTitle('');
    setNoteContent('');
    setIsEditing(true);
    setViewMode('edit'); // Change viewMode to 'edit' when creating a new note
  };
  
  const handleEditNote = () => {
    setIsEditing(true);
    setViewMode('edit'); // Change viewMode to 'edit' when editing a note
  };
  
  const handleSaveNote = async () => {
    if (!noteTitle.trim()) {
      alert('Note title is required');
      return;
    }

    setLoading(true);
    try {
      let savedNote;
      
      if (selectedNote) {
        savedNote = await noteService.updateNote(selectedNote.id, noteTitle, noteContent);
      } else {
        savedNote = await noteService.createNote(noteTitle, noteContent);
      }
      
      // Update local state
      const updatedNotes = selectedNote
        ? notes.map(n => n.id === savedNote.id ? savedNote : n)
        : [...notes, savedNote];
      
      setNotes(updatedNotes);
      setSelectedNote(savedNote);
      setIsEditing(false);
      setViewMode('note');
      
      // Regenerate connections with updated notes
      try {
        const connectionsData = await noteService.getNoteConnections();
        const graphData = {
          nodes: updatedNotes.map(note => ({
            id: note.id,
            title: note.title || 'Untitled',
            val: 1,
            color: "#c8746d"
          })),
          links: connectionsData.length > 0
            ? connectionsData.map(connection => ({
                source: connection.sourceId || connection.source,
                target: connection.targetId || connection.target,
                value: connection.strength || connection.value || 1
              }))
            : generateConnectionsFromNotes(updatedNotes).links
        };
        
        setConnections(graphData);
      } catch (err) {
        console.error('Failed to load connections, using parsed links', err);
        setConnections(generateConnectionsFromNotes(updatedNotes));
      }
    } catch (error) {
      console.error("Error saving note:", error);
      alert("Failed to save note. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteNote = async () => {
    if (!selectedNote) return;
    
    if (window.confirm('Are you sure you want to delete this note?')) {
      setLoading(true);
      try {
        // Delete note via API
        await noteService.deleteNote(selectedNote.id);
        
        // Update local state
        const updatedNotes = notes.filter(n => n.id !== selectedNote.id);
        setNotes(updatedNotes);
        setSelectedNote(null);
        setNoteTitle('');
        setNoteContent('');
        setViewMode('list'); // Return to list view
        
        // Regenerate connections with updated notes
        const connectionsData = await noteService.getNoteConnections();
        if (connectionsData && connectionsData.length > 0) {
          // We have connections from the API
          setConnections({
            nodes: updatedNotes.map(note => ({
              id: note.id,
              title: note.title || 'Untitled',
              val: 1, // Default size value
              color: "#c8746d" // Changed from pink to yellow
            })),
            links: connectionsData.map(connection => ({
              source: connection.sourceId,
              target: connection.targetId,
              value: connection.strength || 1
            }))
          });
        } else {
          // Generate fallback connections
          const graphData = {
            nodes: updatedNotes.map(note => ({
              id: note.id,
              title: note.title || 'Untitled',
              val: 1, // Default size value
              color: "#c8746d" // Changed from pink to yellow
            })),
            links: []
          };
          
          // Create simple chain connections
          if (updatedNotes.length > 1) {
            for (let i = 0; i < updatedNotes.length - 1; i++) {
              graphData.links.push({
                source: updatedNotes[i].id,
                target: updatedNotes[i + 1].id,
                value: 1
              });
            }
          }
          
          setConnections(graphData);
        }
      } catch (error) {
        console.error("Error deleting note:", error);
        alert("Failed to delete note. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Add a new function to handle canceling note creation/editing
  const handleCancelEdit = () => {
    if (selectedNote) {
      setIsEditing(false);
      setNoteTitle(selectedNote.title);
      setNoteContent(selectedNote.content);
      setViewMode('note'); // Return to note view if editing an existing note
    } else {
      setSelectedNote(null);
      setNoteTitle('');
      setNoteContent('');
      setViewMode('list'); // Return to list view if creating a new note
    }
  };
  
  // Highlight matching text in search results
  const highlightMatchedText = (text, query) => {
    if (!query.trim()) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <span className="search-match">{text.substring(index, index + query.length)}</span>
        {text.substring(index + query.length)}
      </>
    );
  };
  
  // Function to convert [[link]] syntax to clickable links
  const formatNoteContent = (content) => {
    if (!content) return '';
    
    const parts = [];
    let lastIndex = 0;
    const linkRegex = /\[\[(.*?)\]\]/g;
    let match;
    
    while ((match = linkRegex.exec(content)) !== null) {
      const linkText = match[1].trim();
      
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      
      // Add the link
      parts.push(
        <a 
          key={match.index}
          href="#" 
          className="note-link"
          onClick={(e) => {
            e.preventDefault();
            const linkedNote = notes.find(n => 
              n.title.toLowerCase() === linkText.toLowerCase()
            );
            if (linkedNote) {
              handleSelectNote(linkedNote);
            }
          }}
        >
          {linkText}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add the remaining text
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    
    return parts;
  };
  
  /* 
   * Comment out node click handler as it's only used for graph view
   *
  const handleNodeClick = (node, event) => {
    if (!node || !node.id) return;
    
    const selectedNoteData = notes.find(note => note.id === node.id);
    if (selectedNoteData) {
      // Set the selected note
      setSelectedNote(selectedNoteData);
      setNoteTitle(selectedNoteData.title);
      setNoteContent(selectedNoteData.content);
      
      // Switch to note view
      setViewMode('note');
      setIsEditing(false);
      
      // Highlight the clicked node
      const updatedNodes = connections.nodes.map(n => ({
        ...n,
        color: n.id === node.id ? '#2DD4BF' : '#c8746d'
      }));
      
      setConnections(prev => ({
        ...prev,
        nodes: updatedNodes
      }));
      
      // Reset node color after animation
      setTimeout(() => {
        setConnections(prev => ({
          ...prev,
          nodes: prev.nodes.map(n => ({
            ...n,
            color: '#c8746d'
          }))
        }));
      }, 1000);
    }
  };
  */
  
  // Adding a new function to handle returning to list view
  const handleReturnToList = () => {
    setViewMode('list');
  };
  
  // Add toggle for notes panel - no longer needed with list view
  // const toggleNotesPanel = () => {
  //   setShowNotes(!showNotes);
  // };
  
  if (loading) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`knowledge-graph-main-content ${theme}`}>
          <div className="sb-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">🧠</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <p className="sb-loading-text">Loading knowledge base...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <SideMenu/>
      
      <div className={`knowledge-graph-main-content ${theme}`}>
        <div className={`knowledge-graph-container ${theme}`}>
          <div className="knowledge-graph-header">
            <div className="panel-title">
              <FileText size={24} />
              <h2 className="knowledge-graph-header-title">Knowledge Base</h2>
            </div>
            <div className="knowledge-graph-header-actions">
              <div className="search-section">
                <div className="search-input-container">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchInputFocus}
                    onClick={handleSearchInputFocus}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    className="knowledge-graph-search-input"
                    autoFocus
                  />
                </div>
                
                {/* Search results dropdown */}
                {searchQuery.trim() && showSearchResults && (
                  <div className="search-results">
                    {searchResults.length > 0 ? (
                      <div>
                        <h3>Search Results</h3>
                        <ul>
                          {searchResults.map((note, index) => (
                            <li 
                              key={note.id}
                              onClick={() => handleSelectNote(note)}
                              className={selectedNote?.id === note.id ? 'active' : ''}
                            >
                              <div className="search-result-title">
                                {highlightMatchedText(note.title, searchQuery)}
                              </div>
                              
                              {note.matchType === 'content' && note.contentPreview && (
                                <div className="search-result-preview">
                                  {highlightMatchedText(note.contentPreview, searchQuery)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="no-results">No results found for "{searchQuery}"</div>
                    )}
                  </div>
                )}
              </div>
              <button 
                className="modern-button primary-button"
                onClick={handleCreateNewNote}
              >
                <Plus size={16} />
                New Note
              </button>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          
          <div className="knowledge-graph-content">
            {/* 
            * Notes button in bottom left - not needed with list view
            * <button className="notes-toggle-button" onClick={toggleNotesPanel}>
            *   {showNotes ? 'Hide Notes' : 'Show Notes'}
            * </button>
            *
            * Notes panel - not needed with list view
            * {showNotes && (
            *   <div className="notes-panel">
            *     <div className="notes-header">
            *       <h3>All Notes</h3>
            *       <button onClick={handleCreateNewNote}>+ New Note</button>
            *     </div>
            *     
            *     <ul className="notes-list">
            *       {notes.map(note => (
            *         <li 
            *           key={note.id}
            *           onClick={() => handleSelectNote(note)}
            *           className={selectedNote?.id === note.id ? 'active' : ''}
            *         >
            *           {note.title || 'Untitled Note'}
            *         </li>
            *       ))}
            *     </ul>
            *   </div>
            * )}
            */}
            
            <div className="knowledge-graph-main">
              {viewMode === 'list' ? (
                <div className="notes-list-view">
                  {notes.length > 0 ? (
                    <>
                      <div className="notes-list-header">
                        <div className="notes-list-header-content">
                          <div className="notes-count-section">
                            <Hash size={20} className="notes-count-icon" />
                            <h3>
                              {selectedTagFilter 
                                ? `Notes with #${selectedTagFilter} (${getFilteredNotes().length})` 
                                : `All Notes (${notes.length})`}
                            </h3>
                          </div>
                          {selectedTagFilter && (
                            <button 
                              className="modern-button danger-button"
                              onClick={() => setSelectedTagFilter(null)}
                            >
                              <X size={14} />
                              Clear Filter
                            </button>
                          )}
                        </div>
                        {getAllUniqueTags().length > 0 && (
                          <div className="notes-list-tags">
                            <div className="tags-header">
                              <Tag size={16} className="tags-icon" />
                              <span className="tags-label">Filter by tags:</span>
                            </div>
                            <div className="notes-list-tags-container">
                              {getAllUniqueTags().map((tag, index) => (
                                <span 
                                  key={index} 
                                  className={`notes-list-tag ${selectedTagFilter === tag ? 'active' : ''}`}
                                  onClick={(e) => handleTagFilterClick(tag, e)}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="notes-grid">
                        {getFilteredNotes().map(note => (
                          <div 
                            key={note.id}
                            className="note-card"
                            onClick={() => handleSelectNote(note)}
                          >
                            <div className="note-card-header">
                              <h3 className="note-card-title">{note.title || 'Untitled Note'}</h3>
                            </div>
                            <p className="note-card-preview">
                              {note.content.length > 150 
                                ? note.content.substring(0, 150) + '...' 
                                : note.content}
                            </p>
                            <div className="note-card-footer">
                              <div className="note-card-bottom-row">
                                <div className="note-card-date">
                                  <Calendar size={12} />
                                  <span>{new Date(note.updatedAt || note.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="note-card-tags">
                                  {getNoteTags(note).map((tag, index) => (
                                    <span 
                                      key={index} 
                                      className="note-card-tag clickable-tag"
                                      onClick={(e) => handleTagClick(tag, e)}
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="empty-graph">
                      <div className="empty-state-icon">📝</div>
                      <h3>No notes yet</h3>
                      <p>Create your first note to start building your knowledge base.</p>
                      <button onClick={handleCreateNewNote} className="modern-button primary-button">
                        <Plus size={16} />
                        Create Note
                      </button>
                    </div>
                  )}
                </div>
              ) : viewMode === 'note' ? (
                <div className={`note-view-full ${theme}`}>
                  <div className="note-view-header">
                    <div className="note-view-title-section">
                      <FileText size={24} />
                      <h1 className="note-view-title">{selectedNote?.title || 'Untitled Note'}</h1>
                    </div>
                    <div className="note-view-actions">
                      <button onClick={handleEditNote} className="modern-button primary-button">
                        <Edit3 size={16} />
                        Edit
                      </button>
                      <button onClick={handleDeleteNote} className="modern-button danger-button">
                        <Trash2 size={16} />
                        Delete
                      </button>
                      <button onClick={handleReturnToList} className="modern-button secondary-button">
                        <ArrowLeft size={16} />
                        Back to Notes
                      </button>
                    </div>
                  </div>
                  <div className="note-view-content">
                    {formatNoteContent(selectedNote?.content)}
                  </div>
                  {selectedNote && getNoteTags(selectedNote).length > 0 && (
                    <div className="note-view-tags">
                      <h4>Tags:</h4>
                      <div className="note-view-tags-list">
                        {getNoteTags(selectedNote).map((tag, index) => (
                          <span 
                            key={index} 
                            className="note-view-tag clickable-tag"
                            onClick={(e) => handleTagClick(tag, e)}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={`note-edit-full ${theme}`}>
                  <div className="note-edit-header">
                    <div className="note-edit-title-section">
                      <Edit3 size={20} />
                      <h3>{selectedNote ? 'Edit Note' : 'Create New Note'}</h3>
                    </div>
                    <div className="note-edit-buttons">
                      <button onClick={handleSaveNote} className="modern-button primary-button">
                        <Save size={16} />
                        Save
                      </button>
                      <button onClick={handleCancelEdit} className="modern-button secondary-button">
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="note-form-full">
                    <input
                      type="text"
                      placeholder="Note Title"
                      value={noteTitle}
                      onChange={(e) => setNoteTitle(e.target.value)}
                      className="note-title-input-full"
                      autoFocus
                    />
                    
                    <textarea
                      ref={editorRef}
                      placeholder="Note content... Use [[]] to create links to other notes and #tag to add tags"
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      className="note-content-input-full"
                    />
                    
                    {/* Tag preview while editing */}
                    {parseTagsFromContent(noteContent).length > 0 && (
                      <div className="note-edit-tags-preview">
                        <h5>Tags in this note:</h5>
                        <div className="note-edit-tags-list">
                          {parseTagsFromContent(noteContent).map((tag, index) => (
                            <span key={index} className="note-edit-tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraph; 